/**
 * Cruscotto Commesse — la sezione che risponde a «come stiamo messi?».
 *
 * Nessuna tabella di commesse: solo i numeri d'insieme, e **ognuno è una
 * porta** che apre il Portafoglio già filtrato o il Magazzino. È la condizione
 * che distingue un cruscotto da una vetrina — e di vetrine, in questo progetto,
 * ne abbiamo già chiuse due (il Cruscotto del #9 e la dashboard logistica).
 */
import { ArrowRight, Boxes, PhoneCall, Truck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { M1Gauge } from "@/components/M1Gauge";
import { ColonneCommesse } from "@/components/m1/ColonneCommesse";
import { ColonneRighe } from "@/components/m1/ColonneRighe";
import { CommessaDrawer } from "@/components/m1/CommessaDrawer";
import { DoveLaMerceFotografia } from "@/components/m1/DoveLaMerceBarra";
import { RigaTesta } from "@/components/RigaTesta";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useM1ArticoliCritici,
  useM1ClientiMerceFerma,
  useM1Dashboard,
  useM1LeadTime,
} from "@/hooks/useM1";
import { num } from "@/lib/numeri";
import { cn } from "@/lib/utils";
import type { M1DashboardKPIs, M1LeadTime, M1StatoCount } from "@/types/api";

/**
 * ⚠️ Il target **non si cabla qui**: arriva da `/m1/lead-time`.
 *
 * Stava in tre posti con due valori — l'endpoint diceva 14, questa pagina cablava
 * 21, il widget di M4 scriveva «≤ 21g» come stringa fissa — e la pagina ignorava
 * quello che l'API mandava: cambiando la soglia nel servizio, la pagina non se ne
 * accorgeva. Ora il numero sta nel servizio e la pagina lo legge.
 */

const STATI = [
  { value: "00 - EVADIBILE PUNTUALE", label: "Puntuali", tono: "ok" },
  { value: "01 - EVADIBILE IN RITARDO", label: "In ritardo", tono: "warn" },
  { value: "02 - PARZIALMENTE EVADIBILE", label: "Parziali", tono: "warn2" },
  { value: "03 - TOTALMENTE INEVADIBILE", label: "Inevadibili", tono: "alert" },
] as const;

const fmt = num;

function byStato(lista: M1StatoCount[] | undefined, stato: string): number {
  return lista?.find((s) => s.stato === stato)?.count ?? 0;
}

/**
 * Un numero che è una porta. Il testo sotto dice **dove porta**: senza quello
 * il riquadro sembra una statistica e nessuno ci clicca.
 */
function Porta({
  label,
  valore,
  dove,
  tono,
  icona: Icona,
  onClick,
  loading,
  inerte,
}: {
  label: string;
  valore: number | string | undefined;
  dove: string;
  tono?: "ok" | "warn" | "warn2" | "alert";
  icona?: LucideIcon;
  onClick?: () => void;
  loading?: boolean;
  inerte?: boolean;
}) {
  const colore =
    tono === "ok"
      ? "text-emerald-700 dark:text-emerald-400"
      : tono === "warn"
        ? "text-amber-700 dark:text-amber-400"
        : tono === "warn2"
          ? "text-orange-700 dark:text-orange-400"
          : tono === "alert"
            ? "text-red-700 dark:text-red-400"
            : "text-foreground";

  return (
    <button
      type="button"
      onClick={inerte ? undefined : onClick}
      disabled={inerte}
      className={cn(
        "group rounded-lg border bg-card p-3 text-left transition-colors",
        inerte ? "cursor-default opacity-70" : "hover:border-brand-600 hover:bg-brand-50/50 dark:hover:bg-brand-950/30"
      )}
    >
      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {Icona && <Icona className="size-3" aria-hidden="true" />}
        {label}
      </span>
      {loading ? (
        <Skeleton className="my-1 h-7 w-12" />
      ) : (
        <span className={cn("block text-2xl font-semibold leading-tight tabular-nums", colore)}>
          {typeof valore === "number" ? fmt(valore) : (valore ?? "—")}
        </span>
      )}
      <span className="mt-0.5 flex items-center gap-1 text-[10px] text-brand-700 dark:text-brand-400">
        {dove}
        {!inerte && (
          <ArrowRight className="size-2.5 opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </span>
    </button>
  );
}

/**
 * Chi tiene ferma la merce già pronta.
 *
 * Risponde alla domanda della direzione — quanto magazzino occupa quel cliente —
 * e non esisteva in nessuna schermata: il dato viveva riga per riga.
 * `quota ferma` distingue il cliente grande da chi ci usa come magazzino.
 */
function ClientiMerceFerma() {
  const { data, isLoading } = useM1ClientiMerceFerma(6);
  const navigate = useNavigate();

  return (
    <section className="rounded-lg border bg-card p-3">
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Chi tiene ferma la merce già pronta
      </h2>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !data?.items.length ? (
        <p className="text-xs text-muted-foreground">
          Nessuna merce pronta in attesa di ritiro.
        </p>
      ) : (
        <>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[10.5px] text-muted-foreground">
                <th className="pb-1.5 text-left font-medium">Cliente</th>
                <th className="pb-1.5 text-right font-medium">Pronta, non ritirata</th>
                <th className="pb-1.5 text-right font-medium">Commesse</th>
                <th className="pb-1.5 pl-3 text-left font-medium">Quota ferma</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.items.map((c) => (
                <tr
                  key={c.cliente_codice}
                  className="cursor-pointer hover:bg-accent/40"
                  onClick={() =>
                    navigate(
                      `/moduli/order-readiness?tab=portafoglio&cliente=${c.cliente_codice}`
                    )
                  }
                  title="Apre il portafoglio di questo cliente"
                >
                  <td className="py-1.5 font-medium">{c.cliente_ragione_sociale ?? "—"}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">
                    {fmt(c.magazzino)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {c.n_commesse}
                  </td>
                  <td className="py-1.5 pl-3">
                    <div className="h-1.5 w-full overflow-hidden rounded-sm bg-muted">
                      <span
                        className="block h-full bg-emerald-600 dark:bg-emerald-500"
                        style={{ width: `${Math.min(c.quota_ferma_pct, 100)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {Math.round(c.quota_ferma_pct)}% del suo ordinato
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Merce già venduta, prodotta e a magazzino: occupa spazio nostro finché il cliente
            non la ritira.
          </p>
        </>
      )}
    </section>
  );
}

/**
 * Il lead time **diviso per stato di ritiro**, che separa due problemi diversi:
 * una commessa a chiamata ha la merce pronta e aspetta il cliente, quindi il suo
 * tempo misura politica commerciale e non produzione.
 *
 * Misurato il 18-08 prima di costruirlo: delle 71 commesse in scala, 32 hanno il
 * 100% del residuo già a magazzino e stanno a 78 g; le 39 che aspettano davvero
 * qualcosa stanno a 47 g. Il numero unico diceva 64.
 */
function LeadTimePerStato({ leadTime }: { leadTime?: M1LeadTime }) {
  const righe = (leadTime?.per_stato_ritiro ?? []).filter((r) => r.n_ordini > 0);
  if (righe.length < 2) return null;
  const esclusi = new Set(leadTime?.stati_esclusi ?? []);
  return (
    <div className="mt-2 space-y-1 border-t pt-2">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        per stato di ritiro
      </p>
      {righe.map((r) => (
        <div key={r.stato} className="flex items-baseline justify-between gap-2 text-[11px]">
          <span className="text-muted-foreground">
            {ETICHETTA_RITIRO[r.stato] ?? r.stato}
            {esclusi.has(r.stato) && (
              /* Lo stato che sta fuori dal numero grande va detto **qui**, accanto
                 al suo numero: altrimenti si legge come un pezzo della mediana
                 sopra e non torna il conto. */
              <span
                className="ml-1 text-[9.5px] text-muted-foreground/70"
                title="La data del ritiro la decide il cliente, non la produzione: questa mediana si legge, ma non entra nel numero grande"
              >
                (fuori dal numero)
              </span>
            )}
          </span>
          <span className="tabular-nums">
            {r.lt_mediana_giorni === null ? (
              <span className="text-muted-foreground/70" title="Nessuna commessa misurabile: tutte hanno una riga inevadibile">
                non misurabile
              </span>
            ) : (
              <>
                <b>{fmt(r.lt_mediana_giorni)}g</b>{" "}
                <span className="text-muted-foreground">su {r.n_ordini}</span>
              </>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Quanto magazzino occupa ciascuno stato di ritiro. È la risposta alla domanda di
 * Enzo che il numero unico non poteva dare: dei pezzi fermi, quanti sono **a
 * chiamata** — cioè il servizio che vendiamo, legittimo — e quanti no, che è un
 * problema. Finché nessuno marca, sta tutto in «standard» e il riquadro lo dice.
 */
function MagazzinoPerStato({ dashboard }: { dashboard?: M1DashboardKPIs }) {
  const righe = (dashboard?.magazzino_per_stato_ritiro ?? []).filter(
    (r) => Number(r.magazzino) > 0,
  );
  if (!righe.length) return null;
  const totale = righe.reduce((a, r) => a + Number(r.magazzino), 0) || 1;
  return (
    <section className="rounded-lg border bg-card p-3">
      <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        La merce ferma, per stato di ritiro{" "}
        <span className="normal-case tracking-normal text-muted-foreground/80">
          — quanta è il servizio che vendiamo e quanta è un problema
        </span>
      </h2>
      <div className="flex h-7 gap-0.5 overflow-hidden rounded">
        {righe.map((r) => (
          <div
            key={r.stato}
            title={`${ETICHETTA_RITIRO[r.stato] ?? r.stato}: ${fmt(r.magazzino)} pezzi su ${r.commesse} commesse`}
            style={{ flex: Number(r.magazzino) / totale }}
            className={cn(
              "flex min-w-0 items-center justify-center overflow-hidden whitespace-nowrap px-2 text-[10.5px] font-medium",
              r.stato === "a_chiamata"
                ? "bg-orange-200 text-orange-900 dark:bg-orange-900 dark:text-orange-100"
                : r.stato === "tassativa"
                  ? "bg-emerald-200 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100"
                  : "bg-muted text-muted-foreground",
            )}
          >
            {fmt(r.magazzino)}
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {righe.map((r) => (
          <span key={r.stato}>
            {ETICHETTA_RITIRO[r.stato] ?? r.stato}: <b className="text-foreground">{fmt(r.magazzino)}</b>{" "}
            pezzi · {r.commesse} commesse
          </span>
        ))}
        {righe.length === 1 && righe[0].stato === "standard" && (
          <span className="italic">
            nessuna commessa è ancora marcata «a chiamata»: finché non lo è, la merce
            ferma non si può distinguere
          </span>
        )}
      </div>
    </section>
  );
}

const ETICHETTA_RITIRO: Record<string, string> = {
  tassativa: "tassative",
  standard: "standard",
  a_chiamata: "a chiamata",
};

export function CruscottoM1({ dashboard }: { dashboard?: M1DashboardKPIs }) {
  const navigate = useNavigate();
  const { isLoading: loadingDash } = useM1Dashboard();
  const { data: leadTime, isLoading: loadingLT } = useM1LeadTime();
  const { data: critici } = useM1ArticoliCritici();
  // La commessa aperta nel drawer. Vive qui e non dentro le colonne: il pannello
  // copre la pagina, non una colonna.
  const [commessaAperta, setCommessaAperta] = useState<number | null>(null);
  // Le stesse tre colonne, due unità di misura. L'interruttore non cambia la
  // domanda — «a che punto siamo» — cambia di cosa si parla: la commessa risponde
  // a Enzo, la riga risponde a chi deve produrre o caricare.
  const [unita, setUnita] = useState<"commesse" | "articoli">("commesse");

  const senzaPiano = critici?.filter(
    (a) => Number(a.piano_in_tempo) + Number(a.piano_in_ritardo) === 0,
  ).length;

  const vaiAlPortafoglio = (params: string) =>
    navigate(`/moduli/order-readiness?tab=portafoglio&${params}`);

  return (
    <div className="space-y-3">
      <RigaTesta
        caricatoIl={dashboard?.snapshot_caricato_il}
        destra="aggiornato all'apertura"
      />

      {/* Le tre colonne stanno **in testa**: sono la lettura per cui si apre la
          pagina. Tutto quello che c'era prima resta sotto. */}
      <div className="flex items-center justify-end gap-1.5 text-[11px]">
        <span className="text-muted-foreground">guarda per</span>
        {(["commesse", "articoli"] as const).map((u) => (
          <button
            key={u}
            type="button"
            onClick={() => setUnita(u)}
            aria-pressed={unita === u}
            className={cn(
              "rounded-full border px-2.5 py-1 font-medium transition-colors",
              unita === u
                ? "border-foreground bg-foreground text-background"
                : "hover:bg-accent",
            )}
          >
            {u === "commesse" ? "Commesse" : "Articoli"}
          </button>
        ))}
      </div>
      {unita === "commesse" ? (
        <ColonneCommesse onApriCommessa={setCommessaAperta} />
      ) : (
        /* ⚠️ Le due viste danno numeri diversi sulla stessa fotografia, ed è
           corretto: 91 commesse su 182 hanno righe in colonne diverse, e la vista
           per commessa le classifica col peggior caso. È il motivo per cui la
           riga esiste — 434 righe pronte, 46.193 pezzi, stanno dentro commesse
           che la prima colonna non conta. */
        <ColonneRighe attiva />
      )}

      <DoveLaMerceFotografia merce={dashboard?.dove_la_merce} />

      {/* ⚠️ Questa fila e le tre colonne qui sopra classificano **le stesse
          commesse su due assi diversi**, e diranno numeri diversi: le colonne
          guardano dove sta la merce, questi quattro stati la confrontano con la
          data di consegna del gestionale. Senza il titolo e la nota sembrano
          contraddirsi; con, sono complementari — ed è l'unico modo onesto di
          tenerli nella stessa pagina, visto che quella data Enrico l'ha
          dichiarata «per lo più fittizia, messa solo per far produrre». */}
      <section className="space-y-1.5">
        <p className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
          Rispetto alla data promessa dal gestionale{" "}
          <span className="font-normal normal-case tracking-normal">
            — spesso messa solo per far produrre, quindi da leggere con prudenza
          </span>
        </p>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {STATI.map((s) => (
            <Porta
              key={s.value}
              label={s.label}
              valore={byStato(dashboard?.ordini_per_stato_maitime, s.value)}
              dove="apre il portafoglio"
              tono={s.tono}
              loading={loadingDash}
              onClick={() => vaiAlPortafoglio(`stato=${encodeURIComponent(s.value)}`)}
            />
          ))}
        </div>
      </section>

      <div className="grid gap-3 lg:grid-cols-[248px_1fr]">
        <div className="h-fit rounded-lg border bg-card p-3">
          <h2 className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Lead time
          </h2>
          {loadingLT || !leadTime ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <M1Gauge
              valore={leadTime.lt_mediana_giorni}
              target={leadTime.target_giorni}
              sogliaGiallo={leadTime.soglia_giallo}
              sogliaRosso={leadTime.soglia_rosso}
              unitaLabel={`su ${fmt(leadTime.n_ordini_in_scala)} commesse di ${fmt(leadTime.n_ordini_aperti)}`}
            />
          )}
          {leadTime && leadTime.n_ordini_fuori_scala > 0 && (
            /* Il denominatore, che prima non usciva dall'API e quindi la pagina
               non poteva dirlo: le commesse con almeno una riga inevadibile sono
               escluse *per costruzione*, quindi più le cose vanno male meno
               commesse entrano nel conto. */
            <p className="mt-2 border-t pt-2 text-[10.5px] leading-snug text-muted-foreground">
              <b>{fmt(leadTime.n_ordini_fuori_scala)}</b> commesse restano fuori dal
              conto: hanno almeno una riga inevadibile, quindi non hanno una data in
              cui la merce sarà pronta.
              {leadTime.n_ordini_esclusi_per_politica > 0 && (
                /* Il secondo motivo di esclusione, che è una **scelta** e non un
                   limite del dato: senza dirlo, 63 + 130 non fa 201 e il lettore
                   attento perde fiducia in tutti gli altri numeri. */
                <>
                  {" "}
                  Altre <b>{fmt(leadTime.n_ordini_esclusi_per_politica)}</b> ne stanno
                  fuori perché sono <b>a chiamata</b>: la data del ritiro la decide il
                  cliente, quindi non misurano la produzione.
                </>
              )}
            </p>
          )}
          <LeadTimePerStato leadTime={leadTime} />
        </div>
        <ClientiMerceFerma />
      </div>

      <MagazzinoPerStato dashboard={dashboard} />

      <section className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Porta
          // L'allineamento M1↔M3 al contrario, che non mostrava nessuna
          // schermata: abbiamo emesso una nota di prelievo su merce che il
          // motore non trova a magazzino. Dieci delle quindici stanno in «Non
          // programmate», con buchi fino a 1.623 pezzi.
          label="NDP senza merce"
          valore={dashboard?.ordini_ndp_senza_merce}
          dove={
            dashboard?.ordini_ndp_senza_merce ? "apre il portafoglio" : "nessuna discordanza"
          }
          tono={dashboard?.ordini_ndp_senza_merce ? "alert" : undefined}
          icona={Truck}
          loading={loadingDash}
          inerte={!dashboard?.ordini_ndp_senza_merce}
          onClick={() => vaiAlPortafoglio("ndp_senza_merce=1")}
        />
        <Porta
          label="Articoli senza piano"
          valore={senzaPiano}
          dove="apre il magazzino"
          tono="alert"
          icona={Boxes}
          loading={senzaPiano === undefined}
          // Ora la porta atterra sul filtro giusto: mostrava 250 e portava su una
          // lista di 315, perché il Magazzino teneva il filtro in `useState`.
          onClick={() => navigate("/moduli/order-readiness?tab=magazzino&magazzino=senza_piano")}
        />
        {/* Era «Commesse a chiamata», che restava a zero perché il gesto per
            marcarle non esisteva in nessuna interfaccia. Ora quel conteggio sta
            nella fila dei riquadri del Portafoglio, dove filtra davvero, e qui
            c'è il segnale che nessuna schermata mostrava: un ritiro fissato con
            la data già passata. */}
        <Porta
          label="Ritiri scaduti"
          valore={dashboard?.ordini_ritiro_scaduto}
          dove={
            dashboard?.ordini_ritiro_scaduto ? "apre il portafoglio" : "nessuno scaduto"
          }
          tono={dashboard?.ordini_ritiro_scaduto ? "alert" : undefined}
          icona={PhoneCall}
          loading={loadingDash}
          inerte={!dashboard?.ordini_ritiro_scaduto}
          onClick={() => vaiAlPortafoglio("ritiro_scaduto=1")}
        />
      </section>

      <CommessaDrawer numero={commessaAperta} onClose={() => setCommessaAperta(null)} />
    </div>
  );
}
